import {
  Accordion,
  BootstrapSizes,
  Box,
  Button,
  ButtonColors,
  Forms,
  Modal,
  TalentechFormFieldTypes,
} from "@talentech/components";
import { useEffect, useRef, useState } from "react";
import { useEditor } from "@grapesjs/react";
import { Editor } from "grapesjs";
import { getOpenGraphValues, OpenGrapProtocolMeta, SoMeImageSizeQuery } from "@/shared/openGraph";
import { CustomCommands } from "@/shared/utils";
import { FormikProps } from "formik";
import clsx from "clsx";
import { toast } from "react-toastify";

const OpenGraphModal: React.FC = () => {
  const editor = useEditor();
  const initialValues = getOpenGraphValues(editor);
  const [showModal, setShowModal] = useState(false);
  const [previewValues, setPreviewValues] = useState(initialValues);

  useEffect(() => {
    initialValues && setPreviewValues(initialValues);
    if (initialValues) {
      Object.entries(initialValues).forEach(([key, value]) => {
        formRef.current?.setFieldValue(key, value);
      });
    }
  }, [initialValues]);
  useEffect(() => {
    editor.Commands.add(CustomCommands.MODAL_OPEN_GRAPH_OPEN, () =>
      setShowModal(true)
    );
    editor.Commands.add(CustomCommands.MODAL_OPEN_GRAPH_CLOSE, () =>
      setShowModal(false)
    );
    editor.Commands.add(
      CustomCommands.MODAL_OPEN_GRAPH_SAVE,
      async (
        editor: Editor,
        _ed: any,
        { form }: { form: OpenGrapProtocolMeta }
      ) => {
        await editor.DataSources.remove("openGraph");
        await editor.DataSources.add({
          id: "openGraph",
          records: [
            {
              id: "meta",
              ...form,
            },
          ],
        });
        setShowModal(false);
      }
    );
  }, []);

  const modalProps = {
    title: "Social Media Sharing & OpenGraph",
    showModal,
    size: BootstrapSizes.XLARGE,
    toggleModal: () => editor.runCommand(CustomCommands.MODAL_OPEN_GRAPH_CLOSE),
    buttons: [
      <Button
        key="cancel"
        onClick={() => editor.runCommand(CustomCommands.MODAL_OPEN_GRAPH_CLOSE)}
        label="Cancel"
      />,
      <Button
        key="save"
        type="submit"
        onClick={() => {
          formRef.current?.handleSubmit();
        }}
        label="Save"
        color={ButtonColors.PRIMARY}
      />,
    ],
  };

  const formProps = {
    handleSubmit: (data: any) =>
      editor.runCommand(CustomCommands.MODAL_OPEN_GRAPH_SAVE, data),
    handleCancel: () => null,
    handleReset: () => null,
    formFields: {
      section: "",
      id: "form",
      fields: [
        {
          field: "title",
          label: "Title",
          type: TalentechFormFieldTypes.TEXT,
          initialValue: previewValues?.title,
          size: {
            xs: 12,
          },
          callback: (value: string) => {
            setPreviewValues((state) => ({
              ...state,
              title: value,
            }));
          },
        },
        {
          field: "description",
          label: "Description",
          type: TalentechFormFieldTypes.TEXT,
          initialValue: previewValues?.description,
          size: {
            xs: 12,
          },
          callback: (value: string) => {
            setPreviewValues((state) => ({
              ...state,
              description: value,
            }));
          },
        },
        {
          field: "url",
          label: "URL",
          type: TalentechFormFieldTypes.TEXT,
          initialValue: previewValues?.url,
          size: {
            xs: 12,
          },
          callback: (value: string) => {
            setPreviewValues((state) => ({
              ...state,
              url: value,
            }));
          },
        },
        {
          field: "image",
          label: "image",
          initialValue: previewValues?.image,
          type: TalentechFormFieldTypes.CUSTOM,
          customComponent: (props: any) => {
            return (
              <Box>
                
                {props.value && <img
                  src={`${props.value}${SoMeImageSizeQuery}`}
                  alt="OpenGraph Image"
                  className="img-fluid mb-2"
                />}
                <Button
                  label="choose image"
                  onClick={() =>
                    editor.Commands.run(CustomCommands.ASSET_MANAGER_OPEN, {
                      onSelect: (src: string) => {
                        props.onChange(src);
                        setPreviewValues((state) => ({
                          ...state,
                          image: src,
                        }));
                      },
                    })
                  }
                />
              </Box>
            );
          },
        },
      ],
    },
  };

  const formRef = useRef<FormikProps<any> | null>(null);

  return (
    <Modal {...modalProps}>
      {showModal && (
        <Box className="row">
          <Box className="col-5">
            {/* @ts-ignore */}
            <Forms.Form {...formProps} reference={formRef} />
          </Box>
          <Box className="col-7">
            <Box className="border">
            {previewValues?.image ? <img
                src={`${previewValues?.image}${SoMeImageSizeQuery}`}
                alt="OpenGraph Image"
                className="img-fluid"
              /> : <Box className="placeholder w-100" style={{
                height: "150px"
              }}></Box>}
              <Box className="p-2 bg-gray-100">
                <p className={clsx("text-uppercase text-muted text-truncate", {
                  "placeholder mb-2 w-100 rounded ": !previewValues?.url
                })}>{previewValues?.url}</p>
                <h4 className={clsx("text-truncate", {
                  "placeholder w-100 rounded": !previewValues?.title
                })}>{previewValues?.title}</h4>
                <p className={clsx("text-truncate", {
                  "placeholder w-100 rounded": !previewValues?.description
                })}>{previewValues?.description}</p>
              </Box>
            </Box>

            <Box className="mt-3">
              <Accordion 
                accordionItems={[{
                  id: "social-media-links",
                  title: "Social Sharing Links",
                  content: <Box>
                    {[
                      {
                        name: "Facebook",
                        url: `https://www.facebook.com/sharer/sharer.php?u=${previewValues?.url}`,
                      },
                      {
                        name: "Twitter",
                        url: `https://twitter.com/intent/tweet?url=${previewValues?.url}`,
                      },
                      {
                        name: "LinkedIn",
                        url: `https://www.linkedin.com/shareArticle?url=${previewValues?.url}`,
                      },
                    ].map((platform) => (
                      <Box key={platform.name}>
                        <h6>{platform.name}</h6>
                        <Box className="d-flex align-items-center gap-2 mb-2 text-break overflow-auto">
                          <Button
                            size={BootstrapSizes.SMALL}
                            onClick={() => {
                              navigator.clipboard.writeText(platform.url);
                              toast.info("Copied to clipboard");
                            }}
                            icon={<i className="fa-light fa-sharp fa-copy" />}
                          />
                        <code className="text-muted">{platform.url}</code>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                }]}
              />
                
            </Box>
          </Box>
        </Box>
      )}
    </Modal>
  );
};

export default OpenGraphModal;
